/**
 * Realtime Channel Recovery
 *
 * Shared helpers for recovering Supabase Realtime subscriptions.
 *
 * Primary responsibilities:
 * - Identify failed subscription statuses
 * - Compute bounded reconnect delays
 *
 * Dependencies:
 * - None
 */

export type RealtimeChannelFailureStatus =
  | 'CHANNEL_ERROR'
  | 'TIMED_OUT'
  | 'CLOSED'

const BASE_RECONNECT_DELAY_MS = 1_000
const MAX_RECONNECT_DELAY_MS = 30_000
const FAILED_STATUSES = new Set<string>([
  'CHANNEL_ERROR',
  'TIMED_OUT',
  'CLOSED'
])
const channelRemovalPromises = new WeakMap<object, Promise<void>>()

type RealtimeChannelClient<TChannel extends object> = {
  removeChannel: (channel: TChannel) => unknown
}

// --------------

export function isRealtimeChannelFailure(
  status: string
): status is RealtimeChannelFailureStatus {
  return FAILED_STATUSES.has(status)
}

// --------------

export function shouldRemoveRealtimeChannel(
  status: RealtimeChannelFailureStatus
) {
  return status !== 'CLOSED'
}

// --------------

export function removeRealtimeChannelOnce<TChannel extends object>(
  client: RealtimeChannelClient<TChannel>,
  channel: TChannel
) {
  const existingRemoval = channelRemovalPromises.get(channel)
  if (existingRemoval) {
    return existingRemoval
  }

  const removal = Promise.resolve(client.removeChannel(channel))
    .catch(() => undefined)
    .then(() => undefined)

  channelRemovalPromises.set(channel, removal)
  return removal
}

// --------------

export function getRealtimeReconnectDelay(attempt: number) {
  const boundedAttempt = Math.min(Math.max(attempt, 0), 5)

  return Math.min(
    MAX_RECONNECT_DELAY_MS,
    BASE_RECONNECT_DELAY_MS * 2 ** boundedAttempt
  )
}
